import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getProfilePhotoUrl, optimizeImage } from "@/lib/mediaUrls";
import { fireTutorialTrigger } from "@/lib/tutorialTriggers";
import { useTutorial } from "@/components/tutorial/TutorialProvider";
import SharedNavigation from "@/components/SharedNavigation";

/**
 * Phase 8 — 🎙️ Live Beams: Audio Rooms list + create.
 *
 * Mobile-first. Lists all `status: live` rooms sorted by gift coins.
 * Logged-in users can create a new room from the inline form. The
 * audio carrier banner makes it explicit that real-time audio is in
 * "preview" — chat, gifting, presence are fully live now.
 */

const RoomCard = ({ room }) => (
  <Link
    to={`/beams/${room.room_id}`}
    className="block p-4 rounded-xl bg-gradient-to-br from-rose-500/10 via-pink-500/10 to-purple-500/10 border border-pink-500/30 hover:border-pink-400 transition-colors relative overflow-hidden"
    data-testid={`live-room-card-${room.room_id}`}
  >
    {room.cover_image && (
      <img
        src={optimizeImage(room.cover_image, 'card')}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover opacity-20 pointer-events-none"
        data-testid={`live-room-card-cover-${room.room_id}`}
        onError={(e) => { e.target.style.display = "none"; }}
      />
    )}
    <div className="relative flex items-center gap-3">
      <div className="relative">
        <img
          src={getProfilePhotoUrl({
            profile_photo: room.host_profile_photo,
            picture: room.host_profile_photo,
          })}
          alt={room.host_name}
          className="w-12 h-12 rounded-full object-cover border-2 border-pink-500/60"
          onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=120&h=120&fit=crop"; }}
        />
        <span className="absolute -top-1 -right-1 text-[9px] bg-rose-500 text-white px-1.5 rounded-full font-bold animate-pulse">
          LIVE
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-bold truncate">{room.title}</p>
        <p className="text-xs text-pink-200/80 truncate">
          Hosted by <span className="text-white font-semibold">{room.host_name || "Anonymous"}</span>
        </p>
        {room.topic && (
          <p className="text-[11px] text-purple-200/70 truncate mt-0.5">
            “{room.topic}”
          </p>
        )}
      </div>
    </div>
    <div className="relative flex items-center justify-between mt-3 text-[11px]">
      <span className="text-emerald-200">
        🟢 {room.online_count || 0} listening
      </span>
      <span className="text-amber-200">
        🪙 {(room.gift_total_coins || 0).toLocaleString()}
      </span>
    </div>
  </Link>
);

const LiveRoomsListPage = () => {
  const navigate = useNavigate();
  const { startSection } = useTutorial();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState("");
  const [topic, setTopic] = useState("");
  const [recordEnabled, setRecordEnabled] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const refresh = async () => {
    try {
      const r = await api.get("/live/rooms?limit=30");
      setRooms(r.data?.rooms || []);
    } catch (_e) {
      setRooms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/auth/me").then((r) => { setAuthed(true); setCurrentUser(r.data?.user || r.data); }).catch(() => setAuthed(false));
    refresh();
    const t = setInterval(refresh, 8000); // light polling so the list feels live
    return () => clearInterval(t);
  }, []);

  const onCreate = async () => {
    if (!title.trim()) { toast.error("Add a title"); return; }
    setCreating(true);
    try {
      const r = await api.post("/live/rooms", { title, topic, record_enabled: recordEnabled });
      // Phase 15.2 #6 — contextual nudge: first beam → invite tip.
      fireTutorialTrigger("first_live", { openTutorial: startSection });
      navigate(`/beams/${r.data.room.room_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create room");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0514] via-[#0a0a14] to-black text-white">
      <SharedNavigation currentUser={currentUser} activePage="beams" />
      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-10 pt-24" data-testid="live-rooms-page">
        {/* Phase 15.6P — Live Beams quick-nav: keep core pages one tap away while in the rooms list */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="beams-quick-nav">
          <Link to="/home" className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-pink-100/90 hover:bg-white/10 transition" data-testid="beams-nav-home">🏠 Home</Link>
          {authed && (
            <Link to="/dashboard" className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-pink-100/90 hover:bg-white/10 transition" data-testid="beams-nav-dashboard">🎛️ Dashboard</Link>
          )}
          {authed && (
            <Link to="/creator-hub" className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-pink-100/90 hover:bg-white/10 transition" data-testid="beams-nav-creator-hub">🎚️ Creator Hub</Link>
          )}
          {authed && currentUser?.user_id && (
            <Link to={`/artist/${currentUser.user_id}`} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-pink-100/90 hover:bg-white/10 transition" data-testid="beams-nav-profile">👤 My Profile</Link>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">
              🎙️ Live Beams
            </h1>
            <p className="text-pink-200/70 text-xs sm:text-sm mt-1">
              Hop into a live room. Talk, chat, gift in real time.
            </p>
          </div>
          {authed && (
            <Button
              onClick={() => navigate("/live?schedule=1")}
              variant="outline"
              className="text-pink-100 border-pink-500/50 hover:bg-pink-500/10 font-bold"
              data-testid="live-rooms-schedule-upcoming"
            >
              📅 Schedule Upcoming
            </Button>
          )}
          {authed && (
            <Button
              onClick={() => setShowCreate((s) => !s)}
              className="bg-gradient-to-r from-rose-500 to-pink-500 text-white font-bold"
              data-testid="live-rooms-create-toggle"
            >
              {showCreate ? "Cancel" : "+ Start Room"}
            </Button>
          )}
        </div>

        {/* Audio carrier banner — sets expectations clearly */}
        <Card className="mb-4 p-3 bg-amber-500/10 border-amber-500/30 text-amber-100 text-xs" data-testid="audio-carrier-banner">
          🎧 <span className="font-semibold">Audio carrier joining beta soon.</span>{" "}
          Chat, presence, and gifting are fully live today — voice streaming rolls out next.
        </Card>

        {showCreate && authed && (
          <Card className="mb-5 p-4 bg-white/5 border-white/10" data-testid="live-rooms-create-form">
            <p className="text-xs uppercase tracking-wider text-pink-200/80 font-bold mb-2">
              Start a Live Beam
            </p>
            <div className="space-y-2">
              <Input
                placeholder="Room title (max 80)"
                value={title}
                maxLength={80}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="live-rooms-create-title"
              />
              <Input
                placeholder="Topic (optional)"
                value={topic}
                maxLength={140}
                onChange={(e) => setTopic(e.target.value)}
                data-testid="live-rooms-create-topic"
              />
              <label className="flex items-center gap-2 cursor-pointer select-none px-1" data-testid="live-rooms-record-toggle-row">
                <input
                  type="checkbox"
                  checked={recordEnabled}
                  onChange={(e) => setRecordEnabled(e.target.checked)}
                  className="w-4 h-4 accent-rose-500"
                  data-testid="live-rooms-create-record"
                />
                <span className="text-xs text-pink-100/90">
                  🎬 <span className="font-bold">Record this room</span>
                  <span className="text-pink-200/60"> — saveable replay (counts against your monthly quota)</span>
                </span>
              </label>
              <Button
                disabled={creating}
                onClick={onCreate}
                className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white font-bold"
                data-testid="live-rooms-create-submit"
              >
                {creating ? "Creating…" : "Go Live 🎙️"}
              </Button>
            </div>
          </Card>
        )}

        {loading ? (
          <p className="text-pink-200/70 text-sm py-10 text-center" data-testid="live-rooms-loading">
            Loading rooms…
          </p>
        ) : rooms.length === 0 ? (
          <Card className="p-8 text-center bg-white/5 border-white/10 text-pink-200/70" data-testid="live-rooms-empty">
            <p className="text-3xl mb-2">📡</p>
            <p className="text-sm">No live rooms right now. {authed ? "Be the first to go live." : "Sign in and start one."}</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="live-rooms-grid">
            {rooms.map((r) => <RoomCard key={r.room_id} room={r} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveRoomsListPage;
